/**
 * SlidingDrawer.js
 * CreatedBy : Davis Yi, Deepesh Aggarwal, Trenton Lawton
 * Create Date : Sep 21, 2017
 * Refactor Date: Oct 3, 2017
 */
import EventEmitter from 'events';
import baseView from './templates/partials/drawer/drawer.hbs';

require(`./scss/_dontExtract_/${process.env.ENTRY_POINT}/drawer.scss`);

const ARIA_BUSY = 'aria-busy';
const DRAWER_CONTAINER_CLASS = 'drawer-container';
const DRAWER_MAIN_CONTAINER = 'drawer-main-wrapper';
const DRAWER_BODY = 'drawer-body';
// const  DRAWER_BACK_CONTAINER = 'drawer-back';
const ESC_KEY = 27;
const TAB_KEY = 9;
// const  SHIFT_KEY = 16;
const ANIMATION_TIME = 500;
const DEFAULT_OPTIONS = {
  slide: 'right',
  title: '',
  content: '',
  closeIcon: false,
  id: '',
  shouldDestroyOnHide: false,
  freezeBody: false,
  hideOnClick: true,
  classList: '',
};

export default class Drawer extends EventEmitter {
  /**
     * @param {Object} options - Cotaining slide' property, and HTML content.
     *                           The value will be appened as class name to drawer-container
     *                           div. slide Property used to slide from left or right.(argument is optional).
     */
  constructor(options) {
    super();
    // Get options and use default if some properties has not provided
    this.options = Object.assign({}, DEFAULT_OPTIONS, options);

    this.el = document.body;

    this.initiateDrawer();
    this.clickEventCallback = this.clickEventCallback.bind(this);
    this.keydownEventCallback = this.keydownEventCallback.bind(this);
  }

  /**
     * body Element is getter function returns shadowDOM of drawer body
     */
  get bodyElement() {
    return this.drawerBody;
  }

  /**
     * Public method which initialize the drawer and creates & prepend a drawer container node in the document.body
     */
  initiateDrawer() {
    const container = document.createElement('div');
    container.innerHTML = baseView({
      'slide-direction': this.options.slide,
      title: this.options.title,
      body: this.options.content,
      classList: this.options.classList,
      closeIcon: this.options.closeIcon,
    });

    this.drawerContainer = container.getElementsByClassName(DRAWER_CONTAINER_CLASS)[0];
    this.drawer = container.getElementsByClassName(DRAWER_MAIN_CONTAINER)[0];
    this.closeButton = container.getElementsByClassName('exit-button')[0];
    this.drawerBody = container.getElementsByClassName(DRAWER_BODY)[0];
    this.el.insertBefore(container, this.el.firstChild);
  }


  /**
     * Public method which shows the drawer on the document.
     */
  showDrawer() {
    const self = this;

    // Tigger event show-drawer
    self.emit('show-drawer');

    this.focusedElBeforeOpen = document.activeElement;
    if (self.drawer.length === 0) {
      self.initiateDrawer(self);
    }

    self.drawerContainer.style.display = 'block';

    // Make Body locked.
    self.el.setAttribute(ARIA_BUSY, 'true');
    self.el.classList.add('stop-body-scroll');

    // Make slide container fade out
    self.drawerContainer.classList.add('show');

    // Make drawer move out
    self.drawer.classList.add('show');

    // Showing our drawer for screenreader and keyboard users
    self.drawer.setAttribute('tabindex', '0');
    self.drawer.setAttribute('aria-hidden', 'false');

    // setting our focus to the drawer
    this.drawer.focus();
    self.addEventListener();
  }


  /**
     * Public method which hides the drawer-container div which is alread created by showdrawer method.
     */

  // TODO Hide set dialog to tabindex="-1" and aria-hidden
  hideDrawer() {
    const self = this;

    self.emit('hide-drawer');

    // Make body move again
    if (this.options.freezeBody === false) {
      self.el.setAttribute(ARIA_BUSY, 'false');
      self.el.classList.remove('stop-body-scroll');
    }

    // Make slide container fade out
    self.drawerContainer.classList.remove('show');

    // Make drawer move out
    self.drawer.classList.remove('show');

    // Hiding our drawer for keyboard and screen reader users
    self.drawer.setAttribute('tabindex', '-1');
    self.drawer.setAttribute('aria-hidden', 'true');

    if (self.options.shouldDestroyOnHide) {
      // Setting timeout to destroy after the drawer fade out animation finishes.
      // Otherwise we won't see the fade out.
      setTimeout(self.destroy.bind(self), ANIMATION_TIME);
    }
    // master focus reset upon drawer close
    self.focusedElBeforeOpen.focus();
  }

  /**
     * Public method that add event listener to the drawer container
     */
  addEventListener() {
    const self = this;
    self.drawerContainer.addEventListener('click', self.clickEventCallback);
    // checking that we even need to add the event listener for the title close

    if (this.options.title !== null && this.options.title !== '') {
      self.closeButton.addEventListener('click', self.clickEventCallback);
    }

    self.drawer.addEventListener('keydown', self.keydownEventCallback);
  }


  /**
     *  Public method that remove event listener from the drawer container
     */
  destroy() {
    const self = this;
    self.drawerContainer.removeEventListener('click', self.clickEventCallback);
    if (self.closeButton) {
      self.closeButton.removeEventListener('click', self.clickEventCallback);
    }
    self.drawer.removeEventListener('keydown', self.keydownEventCallback);

    // Goodbye drawer markup. We want to clear the dom nodes that are relevant to drawer
    if (self.drawerContainer.parentNode) {
      self.drawerContainer.parentNode.removeChild(self.drawerContainer);
    } else {
      self.drawerContainer.remove();
    }
    if (self.drawer.parentNode) {
      self.drawer.parentNode.removeChild(self.drawer);
    } else {
      self.drawer.remove();
    }

    // Make body move again
    if (this.options.freezeBody === false) {
      self.el.setAttribute(ARIA_BUSY, 'false');
      self.el.classList.remove('stop-body-scroll');
    }
  }

  /**
     * This function is a callback function for the click event.
     * @param {Object} event - DOM event
     */
  clickEventCallback(event) {
    event.preventDefault();
    this.hideDrawer();
  }

  /**
     * This function is a callback function for the keypress event for accessibility
     * @param {Object} event - DOM event
     */
  keydownEventCallback(event) {
    const self = this;
    const query = 'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]';

    const focusElements = this.drawer.querySelectorAll(query);
    self.focusableElements = Array.prototype.slice.call(focusElements);
    const firstElement = self.focusableElements[0];
    const lastElement = self.focusableElements[self.focusableElements.length - 1];

    // TODO: accessibility for moving focus back to last item on escape.
    if (ESC_KEY === event.keyCode) {
      self.hideDrawer();
      event.preventDefault();
    } else if (TAB_KEY === event.keyCode) {
      if (self.focusableElements.length === 1) {
        return;
      }

      if (event.shiftKey) {
        if (document.activeElement === self.focusableElements[0]) {
          event.preventDefault();
          lastElement.focus();
        }
      } else if (document.activeElement === self.focusableElements[self.focusableElements.length - 1]) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }
}
